import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, ChevronLeft, ChevronRight, Wallet, Clock } from 'lucide-react';
import useBillingStore from '../../../store/billing';
import useAuth from '../../../hooks/useAuth';
import { getActiveClientsApi } from '../../../api/company';
import { getDeliveryUsers } from '../../../api/user';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { PageHeader } from '../../../components/ui/PageHeader';
import { EmptyState } from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { RelativeTime } from '../../../components/ui/RelativeTime';
import { useIsMobile } from '../../../hooks/useIsMobile';
import StatusBadge from './components/StatusBadge';
import GeneratePreDocModal from './components/GeneratePreDocModal';
import { formatCurrency, formatDate, TYPE_LABELS } from './utils/billingHelpers';

// Estados que cuentan como "pendientes de cierre" (aún no facturados/cerrados).
const PENDING_STATUSES = ['draft', 'reviewed', 'approved'];

// Normaliza un valor que puede venir como id string o como objeto poblado.
const idOf = (v) => (v && typeof v === 'object' ? v._id : v);

const TYPE_OPTIONS = [
  { value: '', labelKey: 'Todos' },
  { value: 'client_invoice', labelKey: 'Factura a Cliente' },
  { value: 'driver_payment', labelKey: 'Pago a Repartidor' },
];

const STATUS_OPTIONS = [
  { value: '', labelKey: 'Todos' },
  { value: 'draft', labelKey: 'Borrador' },
  { value: 'reviewed', labelKey: 'Revisado' },
  { value: 'approved', labelKey: 'Aprobado' },
  { value: 'sent_to_bsale', labelKey: 'Enviado a BSale' },
  { value: 'invoiced', labelKey: 'Facturado' },
  { value: 'rejected', labelKey: 'Rechazado' },
];

// columnas: Tipo · Cliente/Repartidor · Periodo · Entregas · Total · Estado · Creado · Acciones
const GRID_COLS = '1.2fr 1.4fr 1.3fr 0.7fr 0.9fr 0.9fr 0.9fr auto';

const PreDocumentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id ?? null;
  const {
    preDocuments,
    preDocumentsCount,
    loading,
    filters,
    setFilters,
    fetchPreDocuments,
  } = useBillingStore();

  const [showModal, setShowModal] = useState(false);
  // Diccionarios id → nombre para resolver companyId/driverId en la tabla.
  const [clientMap, setClientMap] = useState({});
  const [driverMap, setDriverMap] = useState({});

  useEffect(() => {
    fetchPreDocuments();
  }, [filters]);

  // Carga los mismos catálogos que usa GeneratePreDocModal para mostrar nombres.
  useEffect(() => {
    if (!logisticCompanyId) return;
    let active = true;

    (async () => {
      try {
        const clients = await getActiveClientsApi(logisticCompanyId);
        if (active && Array.isArray(clients)) {
          setClientMap(Object.fromEntries(clients.map((c) => [c._id, c.name || ''])));
        }
      } catch { /* nombres opcionales: si falla, se muestra el fallback */ }

      try {
        const { docs } = await getDeliveryUsers('active', 'delivery', logisticCompanyId);
        if (active && Array.isArray(docs)) {
          setDriverMap(
            Object.fromEntries(
              docs.map((d) => [d._id, `${d.name ?? ''} ${d.lastName ?? ''}`.trim()]),
            ),
          );
        }
      } catch { /* nombres opcionales */ }
    })();

    return () => { active = false; };
  }, [logisticCompanyId]);

  // Resuelve el nombre de la entidad (cliente o repartidor) de un pre-documento.
  const resolveEntity = (doc) => {
    if (doc.type === 'driver_payment') {
      if (doc.driver?.name || doc.driver?.lastName) {
        return `${doc.driver.name ?? ''} ${doc.driver.lastName ?? ''}`.trim();
      }
      return driverMap[idOf(doc.driverId)] || null;
    }
    if (doc.company?.name) return doc.company.name;
    return clientMap[idOf(doc.companyId)] || null;
  };

  // Strip de totales/pendientes derivado de los resultados visibles.
  const strip = useMemo(() => {
    const total = preDocuments.reduce((acc, d) => acc + (d.totalAmount ?? 0), 0);
    const pending = preDocuments.filter((d) => PENDING_STATUSES.includes(d.status)).length;
    return { total, pending, count: preDocuments.length };
  }, [preDocuments]);

  const handleFilterChange = (e) => {
    setFilters({ [e.target.name]: e.target.value });
  };

  const handlePageChange = (newOffset) => {
    setFilters({ offset: newOffset });
  };

  const totalPages = Math.ceil(preDocumentsCount / (filters.limit ?? 25));
  const currentPage = Math.floor((filters.offset ?? 0) / (filters.limit ?? 25)) + 1;

  const goToDoc = (id) => navigate(`/admin/billing/pre-documents/${id}`);

  return (
    <div className="bill-page">
      <PageHeader
        className="mb-6"
        icon={<FileText className="w-6 h-6 text-fg-muted" />}
        title={t('Pre-documentos de Facturación')}
        subtitle={`${preDocumentsCount} ${preDocumentsCount === 1 ? t('documento encontrado') : t('documentos encontrados')}`}
        actions={
          <Button onClick={() => setShowModal(true)} iconLeft={<Plus className="w-4 h-4" />}>
            {t('Generar Pre-documento')}
          </Button>
        }
      />

      {/* Filtros */}
      <div className="bill-card bill-card--clip">
        <div className="bill-filters">
          <div className="bill-field">
            <label className="bill-label">{t('Tipo')}</label>
            <Select name="type" value={filters.type} onChange={handleFilterChange}>
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </Select>
          </div>
          <div className="bill-field">
            <label className="bill-label">{t('Estado')}</label>
            <Select name="status" value={filters.status} onChange={handleFilterChange}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </Select>
          </div>
          <div className="bill-field">
            <label className="bill-label">{t('Periodo desde')}</label>
            <Input type="date" name="periodStartFrom" value={filters.periodStartFrom} onChange={handleFilterChange} />
          </div>
          <div className="bill-field">
            <label className="bill-label">{t('Periodo hasta')}</label>
            <Input type="date" name="periodStartTo" value={filters.periodStartTo} onChange={handleFilterChange} />
          </div>
        </div>
      </div>

      {/* Strip de totales / pendientes (derivado de los resultados visibles) */}
      {!loading && preDocuments.length > 0 && (
        <div className="bill-strip" role="group" aria-label={t('Resumen de resultados')}>
          <div className="bill-strip__item">
            <span className="bill-strip__icon"><FileText className="w-4 h-4" /></span>
            <span className="bill-strip__label">{t('En esta página')}</span>
            <span className="bill-strip__value">{strip.count}</span>
          </div>
          <div className="bill-strip__item">
            <span className="bill-strip__icon bill-strip__icon--accent"><Wallet className="w-4 h-4" /></span>
            <span className="bill-strip__label">{t('Total visible')}</span>
            <span className="bill-strip__value bill-td-amount">{formatCurrency(strip.total)}</span>
          </div>
          <div className="bill-strip__item">
            <span className="bill-strip__icon bill-strip__icon--warning"><Clock className="w-4 h-4" /></span>
            <span className="bill-strip__label">{t('Pendientes')}</span>
            <span className="bill-strip__value">{strip.pending}</span>
          </div>
        </div>
      )}

      {/* Tabla / Cards */}
      <div className="bill-card bill-card--clip">
        {loading ? (
          <TableSkeleton rows={8} columns={8} className="border-0" />
        ) : preDocuments.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title={t('No hay pre-documentos')}
            description={t('No se encontraron pre-documentos con los filtros seleccionados')}
          />
        ) : isMobile ? (
          <div className="bill-cards">
            {preDocuments.map((doc) => (
              <div
                key={doc._id}
                className="bill-card-item bill-card-item--clickable"
                role="button"
                tabIndex={0}
                onClick={() => goToDoc(doc._id)}
                onKeyDown={(e) => { if (e.key === 'Enter') goToDoc(doc._id); }}
              >
                <div className="bill-card-item__head">
                  <span className="bill-card-item__title">{t(TYPE_LABELS[doc.type] ?? doc.type)}</span>
                  <StatusBadge status={doc.status} size="sm" />
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">
                      {doc.type === 'driver_payment' ? t('Repartidor') : t('Cliente')}
                    </span>
                    <span className="bill-card-item__v bill-td-strong">
                      {resolveEntity(doc) ?? <span className="bill-td-sub">—</span>}
                    </span>
                  </div>
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Periodo')}</span>
                    <span className="bill-card-item__v bill-td-muted">
                      {formatDate(doc.periodStart)} → {formatDate(doc.periodEnd)}
                    </span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Entregas')}</span>
                    <span className="bill-card-item__v bill-td-strong">{doc.lineItems?.length ?? 0}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Total')}</span>
                    <span className="bill-card-item__v bill-td-amount">{formatCurrency(doc.totalAmount)}</span>
                  </div>
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Creado')}</span>
                    <span className="bill-card-item__v bill-td-sub">
                      {doc.createdAt ? <RelativeTime value={doc.createdAt} t={t} /> : '-'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': GRID_COLS }}>
            <div className="bill-grid__head" role="row">
              <span className="bill-grid__hcell" role="columnheader">{t('Tipo')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Cliente / Repartidor')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Periodo')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Entregas')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Total')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Estado')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Creado')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Acciones')}</span>
            </div>
            {preDocuments.map((doc) => (
              <div
                key={doc._id}
                className="bill-grid__row bill-grid__row--clickable"
                role="row"
                onClick={() => goToDoc(doc._id)}
              >
                <span className="bill-td-strong" role="cell">{t(TYPE_LABELS[doc.type] ?? doc.type)}</span>
                <span className="bill-td-strong" role="cell">
                  {resolveEntity(doc) ?? <span className="bill-td-sub">—</span>}
                </span>
                <span className="bill-td-muted" role="cell">
                  {formatDate(doc.periodStart)}
                  <br />
                  <span className="bill-td-sub">→ {formatDate(doc.periodEnd)}</span>
                </span>
                <span className="bill-td-strong" role="cell">{doc.lineItems?.length ?? 0}</span>
                <span className="bill-td-amount" role="cell">{formatCurrency(doc.totalAmount)}</span>
                <span role="cell"><StatusBadge status={doc.status} /></span>
                <span className="bill-td-sub" role="cell">{doc.createdAt ? <RelativeTime value={doc.createdAt} t={t} /> : '-'}</span>
                <span className="bill-grid__cell--actions" role="cell" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" onClick={() => goToDoc(doc._id)}>
                    {t('Ver')}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => handlePageChange((currentPage - 2) * filters.limit)}
              aria-label={t('Anterior')}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>{t('Página')} {currentPage} {t('de')} {totalPages}</span>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage * filters.limit)}
              aria-label={t('Siguiente')}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      <GeneratePreDocModal isOpen={showModal} toggle={() => setShowModal(false)} />
    </div>
  );
};

export default PreDocumentList;
